/**
 * ------------------------------------------------------------------------------------------------
 * CI SHELL
 * ------------------------------------------------------------------------------------------------
 *
 * Left navigation: pointer-drag show/hide, snap, persistence, and the content translate.
 *
 * Written as a custom element to match Focal's own idiom rather than introducing a second pattern.
 * The theme is untouched — removing the script tag in theme.liquid leaves a nav that is simply
 * always visible, styled by CSS alone.
 *
 * Phase 1, step 1.2. See PLAN.md.
 */

(() => {
  const STORAGE_KEY = 'ci-nav-open';
  const MOBILE_QUERY = '(max-width: 740px)';

  /* Movement below this is a click, not a drag — so a press on a link still navigates. */
  const DRAG_THRESHOLD = 4;

  /* Snap open/closed past 40% of the nav width... */
  const SNAP_RATIO = 0.4;

  /* ...or on a fast flick, so a short quick gesture still works. */
  const FLICK_VELOCITY = 0.4; // px/ms

  /**
   * Session storage, not local.
   *
   * The nav is meant to be open on desktop: it is the site's navigation, and a visitor arriving to a
   * page with it hidden has to discover the edge tab before they can go anywhere. Remembering
   * "closed" forever meant one dismissal, months ago, decided how the site greeted someone every time
   * after.
   *
   * Scoped to the session it still respects the choice while they are here — close it, follow three
   * links, it stays closed — and the next visit starts open again.
   */
  const readStored = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };

  const writeStored = (open) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, open ? '1' : '0');
    } catch (e) {
      /* Private browsing or blocked storage — state simply does not persist. */
    }
  };

  class CiNav extends HTMLElement {
    connectedCallback() {
      this.root = document.documentElement;
      this.mq = window.matchMedia(MOBILE_QUERY);
      this.content = document.querySelector('[data-ci-content]');
      this.toggle = document.querySelector('[data-ci-nav-toggle]');
      this.backdrop = document.querySelector('[data-ci-nav-backdrop]');
      this.edge = document.querySelector('[data-ci-nav-edge]');

      this.dragging = false;
      this.pointerId = null;

      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onBreakpointChange = this._onBreakpointChange.bind(this);

      this.addEventListener('pointerdown', this._onPointerDown);
      if (this.edge) this.edge.addEventListener('pointerdown', this._onPointerDown);

      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen));
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.setOpen(false));
      }

      document.addEventListener('keydown', this._onKeydown);
      this.mq.addEventListener('change', this._onBreakpointChange);

      this._sync();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      this.mq.removeEventListener('change', this._onBreakpointChange);
    }

    get isMobile() {
      return this.mq.matches;
    }

    get isOpen() {
      return !this.root.classList.contains('ci-nav-closed');
    }

    get navWidth() {
      return this.offsetWidth || 320;
    }

    /**
     * Reflect state into the DOM. Focusability follows visibility via `inert`, so a hidden nav is
     * not reachable by keyboard; aria-expanded is kept in sync on the toggle.
     */
    _sync() {
      const open = this.isOpen;

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', String(open));
      }

      /* On mobile the nav is an overlay; when closed it should not be reachable at all. */
      if (open) {
        this.removeAttribute('inert');
      } else {
        this.setAttribute('inert', '');
      }
    }

    setOpen(open, { persist = true } = {}) {
      this.root.classList.toggle('ci-nav-closed', !open);

      /* Mobile is an overlay and always starts closed on load, so its state is not persisted. */
      if (persist && !this.isMobile) writeStored(open);

      this._sync();

      if (open && this.isMobile) {
        const first = this.querySelector('a, button');
        if (first) first.focus();
      }
    }

    _onBreakpointChange() {
      this._clearDragStyles();

      if (this.isMobile) {
        /* Never leave a persisted desktop "open" as an overlay covering a phone screen. */
        this.setOpen(false, { persist: false });
      } else {
        const stored = readStored();
        this.setOpen(stored === null ? true : stored === '1', { persist: false });
      }
    }

    _onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.isMobile || !this.isOpen) return;

      this.setOpen(false);
      if (this.toggle) this.toggle.focus();
    }

    /* ---------------------------------------------------------------------------------------- */
    /* Drag                                                                                      */
    /* ---------------------------------------------------------------------------------------- */

    _onPointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;

      this.pointerId = event.pointerId;
      this.pointerType = event.pointerType;

      /* A press that never becomes a drag still means "open" when it lands on the edge tab. */
      this.fromEdge = event.currentTarget === this.edge;

      /* ...and "close" when it lands on the one inside. */
      this.fromCloseTab = !!(event.target.closest && event.target.closest('[data-ci-nav-close]'));

      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      this.lastT = event.timeStamp;
      this.velocity = 0;
      this.startProgress = this.isOpen ? 1 : 0;
      this.dragging = false;
      this.moved = false;

      window.addEventListener('pointermove', this._onPointerMove, { passive: false });
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;

      if (!this.dragging) {
        /* Wait until the gesture has committed to a direction before judging it. Testing this on
           the very first move would abort on the diagonal jitter that starts most mouse drags. */
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;

        /* On touch, a mostly-vertical gesture belongs to page scrolling. A mouse has no such
           ambiguity — it never scrolls by dragging — so it is never handed back. */
        if (this.pointerType !== 'mouse' && Math.abs(dy) > Math.abs(dx)) {
          return this._endPointer();
        }

        if (Math.abs(dx) < DRAG_THRESHOLD) return;

        this.dragging = true;
        this.moved = true;
        this.root.classList.add('ci-nav-dragging');
      }

      event.preventDefault();

      const dt = event.timeStamp - this.lastT;
      if (dt > 0) this.velocity = (event.clientX - this.lastX) / dt;
      this.lastX = event.clientX;
      this.lastT = event.timeStamp;

      const progress = Math.min(1, Math.max(0, this.startProgress + dx / this.navWidth));
      this._applyProgress(progress);
    }

    _onPointerUp() {
      /**
       * A click on the edge tab opens the nav.
       *
       * The tab is the only thing telling anyone a hidden nav can come back, so it has to answer the
       * gesture people will actually try. Dragging was the only way to work it, which meant the one
       * visible affordance did nothing when clicked.
       */
      if (!this.dragging && this.fromEdge) {
        this.setOpen(true);
        return this._endPointer();
      }

      /* The same, from the inside. */
      if (!this.dragging && this.fromCloseTab) {
        this.setOpen(false);
        return this._endPointer();
      }

      if (!this.dragging) return this._endPointer();

      const dx = this.lastX - this.startX;
      const progress = Math.min(1, Math.max(0, this.startProgress + dx / this.navWidth));

      let open;
      if (Math.abs(this.velocity) > FLICK_VELOCITY) {
        open = this.velocity > 0;
      } else {
        open = progress > SNAP_RATIO;
      }

      this._clearDragStyles();
      this.setOpen(open);
      this._endPointer();
    }

    _endPointer() {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);

      /* Swallow the click that follows a real drag so it does not also follow a link. */
      if (this.moved) {
        const swallow = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
      }

      this.dragging = false;
      this.moved = false;
      this.pointerId = null;
    }

    /**
     * Only ever writes transform. Never width, margin, or any layout property — dragging must not
     * trigger reflow. Content moves half the nav's distance so it lands centred in the space left.
     */
    _applyProgress(progress) {
      this.style.transform = `translateX(${(progress - 1) * this.navWidth}px)`;

      if (this.content && !this.isMobile) {
        this.content.style.transform = `translateX(${(progress * this.navWidth) / 2}px)`;
      }
    }

    _clearDragStyles() {
      this.root.classList.remove('ci-nav-dragging');
      this.style.transform = '';
      if (this.content) this.content.style.transform = '';
    }
  }

  if (!customElements.get('ci-nav')) {
    customElements.define('ci-nav', CiNav);
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Carousel drag-to-scroll                                                                     */
  /* ------------------------------------------------------------------------------------------ */

  /**
   * Lets a mouse drag the product rows the way a finger already can. Touch is deliberately left
   * alone — the browser's own momentum scrolling is better than anything reimplemented here.
   */
  function enableDragScroll(scroller) {
    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;

    scroller.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = scroller.scrollLeft;
      dragging = false;
      moved = false;
    });

    scroller.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;

      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        moved = true;
        document.documentElement.classList.add('ci-row-dragging');
        scroller.setPointerCapture(pointerId);
      }

      event.preventDefault();
      scroller.scrollLeft = startScroll - dx;
    });

    const endDrag = () => {
      if (pointerId !== null && scroller.hasPointerCapture(pointerId)) {
        scroller.releasePointerCapture(pointerId);
      }

      document.documentElement.classList.remove('ci-row-dragging');

      /* Swallow the click that follows a real drag, so it does not open a product. */
      if (moved) {
        const swallow = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        scroller.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => scroller.removeEventListener('click', swallow, { capture: true }), 0);
      }

      pointerId = null;
      dragging = false;
      moved = false;
    };

    scroller.addEventListener('pointerup', endDrag);
    scroller.addEventListener('pointercancel', endDrag);

    /*
     * Also on the window. If a release ever lands outside the scroller — capture failing, the
     * pointer leaving the window, a re-render mid-drag — the scroller's own handlers never fire and
     * the dragging class stays on <html> for the rest of the visit, leaving text unselectable.
     */
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }

  /**
   * Edge fades, showing there is more row in a direction. Recomputed on scroll and on resize, and
   * once on load for the initial state — a full row starts with a fade on the right only.
   */
  function trackScrollEdges(scroller) {
    const update = () => {
      const max = scroller.scrollWidth - scroller.clientWidth;

      /* A row that fits entirely gets no fades at all. */
      if (max <= 1) {
        scroller.classList.remove('has-fade-start', 'has-fade-end');
        return;
      }

      scroller.classList.toggle('has-fade-start', scroller.scrollLeft > 1);
      scroller.classList.toggle('has-fade-end', scroller.scrollLeft < max - 1);
    };

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    /* Images settle after load and change scrollWidth, so re-measure then too. */
    window.addEventListener('load', update);

    update();
  }

  /**
   * Every row that scrolls sideways, whenever it turns up.
   *
   * Sideways scrolling on a desktop is shift-plus-wheel, which is not something to expect a visitor
   * to know. Dragging is the gesture people try unprompted, so every such row should answer it —
   * and the fades and grab cursor should say so before anyone has to guess.
   *
   * Wired by scanning rather than by a single pass at load, because two of these rows do not exist
   * at load: the tours artist row and the web store's band row are rendered by their islands
   * afterwards, and the shop renders its row again when the rest of the catalogue arrives. The old
   * one-time pass is why those two rows had no drag at all while the product rows did.
   */
  const SCROLLER_SELECTOR = '.ci-row__scroller, .ci-tours__scroller';

  /* Rows already wired, so re-scanning never stacks a second set of listeners on one of them. */
  const wired = new WeakSet();

  const initScroller = (scroller) => {
    if (wired.has(scroller)) return;
    wired.add(scroller);

    enableDragScroll(scroller);
    trackScrollEdges(scroller);
  };

  const scan = (root) => {
    if (root instanceof Element && root.matches(SCROLLER_SELECTOR)) initScroller(root);
    if (root.querySelectorAll) root.querySelectorAll(SCROLLER_SELECTOR).forEach(initScroller);
  };

  scan(document);

  /* The islands mount and re-render on their own schedule; watching for them is simpler than being
     told, and it keeps this side of the wiring from having to know they exist. */
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
